import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building, Mail, Lock, Hash, School, Users, Calendar, Target,
  ArrowRight, Briefcase, GraduationCap, Award
} from 'lucide-react';

const RegisterForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/college/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      await response.json();
      navigate('/login');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Users className="w-6 h-6 text-blue-600" />,
      title: "Alumni Network",
      description: "Build and manage your institution's alumni community"
    },
    {
      icon: <Briefcase className="w-6 h-6 text-orange-500" />,
      title: "Career Portal",
      description: "Connect students with alumni job opportunities"
    },
    {
      icon: <Calendar className="w-6 h-6 text-green-600" />,
      title: "Event Management",
      description: "Organize and track alumni gatherings and reunions"
    },
    {
      icon: <Award className="w-6 h-6 text-blue-600" />,
      title: "Analytics Dashboard",
      description: "Track engagement and alumni success metrics"
    }
  ];

  return (
    <div className="h-screen bg-gradient-to-br from-white via-orange-50 to-blue-50 overflow-hidden">
      <div className="fixed top-0 right-0 w-64 h-64 bg-orange-100 rounded-full blur-3xl opacity-20 -mr-32 -mt-32" />
      <div className="fixed bottom-0 left-0 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-20 -ml-32 -mb-32" />
      <div className="fixed top-1/2 left-1/2 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-10 -translate-x-1/2 -translate-y-1/2" />

      <div className="flex h-screen">
        {/* Left Side - Registration Form */}
   <div className="w-1/2 px-8 py-6 overflow-auto flex items-center justify-center">
        <div className="max-w-md w-full">
            <div className="mb-6 relative">
              <h1 className="text-3xl font-bold text-gray-900 font-sans mt-6">
                Register Your Institution
              </h1>
              <p className="mt-2 text-base text-gray-600">
                Join Saarthi to manage your alumni network effectively
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Institution Name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Building className="h-5 w-5 text-blue-600" />
                  </div>
                  <input
                    type="text"
                    required
                    className="pl-10 w-full rounded-lg border border-gray-300 bg-white py-2 text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Institution Code
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Hash className="h-5 w-5 text-blue-600" />
                  </div>
                  <input
                    type="text"
                    required
                    className="pl-10 w-full rounded-lg border border-gray-300 bg-white py-2 text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Official Email
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <input
                    type="email"
                    required
                    className="pl-10 w-full rounded-lg border border-gray-300 bg-white py-2 text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Lock className="h-5 w-5 text-blue-600" />
                  </div>
                  <input
                    type="password"
                    required
                    className="pl-10 w-full rounded-lg border border-gray-300 bg-white py-2 text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center py-3 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 transition-colors duration-200"
              >
                {loading ? (
                  'Creating your account...'
                ) : (
                  <>
                    Register Institution
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </form>
            <div className="text-center text-sm text-gray-500 mb-4 mt-6">
                Already registered?{' '}
                <a href="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                  Login
                </a>
              </div>
          </div>
        </div>

        {/* Right Side - Features & Benefits */}
        <div className="w-1/2 bg-gradient-to-br from-gray-50 to-white px-8 py-6 flex items-center relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-gray-100/50 z-0" />
          <div className="absolute top-0 right-0 h-full w-1 bg-gradient-to-b from-orange-500 via-blue-600 to-green-500" />

          <div className="max-w-lg mx-auto relative z-10">
            <div className="text-center mb-8">
              <div className="relative inline-block">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-200 via-blue-200 to-green-200 rounded-full blur opacity-30 animate-pulse" />
                <img 
                  src="https://i.ibb.co/JB1mSr9/SAARTHI-removebg-preview.png"
                  alt="Saarthi Logo"
                  className="relative w-32 h-32 object-contain"
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 mt-4">
                Welcome to Saarthi for Institutions
              </h2>
              <p className="text-base text-gray-600">
                Empower Your Alumni Network
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group hover:border-blue-100"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 p-2 rounded-lg bg-gradient-to-br from-blue-50 to-white group-hover:from-blue-100 transition-colors">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {feature.title}
                      </h3>
                      <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-orange-50 to-white p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-orange-600">100+</div>
                <div className="text-sm text-gray-600">Partner Institutions</div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">50k+</div>
                <div className="text-sm text-gray-600">Active Alumni</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-white p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">1000+</div>
                <div className="text-sm text-gray-600">Events Organized</div>
              </div>
            </div>

            <div className="mt-2 text-center text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
             
              <p>By registering, you agree to our</p>
              <div className="mt-1 space-x-2 font-medium">
                <a href="/terms" className="text-blue-600 hover:text-blue-700">Terms</a>
                <span>•</span>
                <a href="/privacy" className="text-blue-600 hover:text-blue-700">Privacy</a>
                <span>•</span>
                <a href="/community" className="text-blue-600 hover:text-blue-700">Guidelines</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;